/**
 * Превью прикреплённых файлов для AI чата
 */

import { X, FileIcon, Image, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Attachment } from './types';

interface AttachmentPreviewProps {
  attachments: Attachment[];
  onRemove: (id: string) => void;
  className?: string;
}

/**
 * Определяет тип файла для иконки
 */
function getFileType(mimeType: string): 'image' | 'document' | 'other' {
  if (mimeType.startsWith('image/')) {return 'image';}
  if (
    mimeType === 'application/pdf' ||
    mimeType.includes('document') ||
    mimeType.includes('text')
  ) {
    return 'document';
  }
  return 'other';
}

/**
 * Форматирование размера файла
 */
function formatFileSize(bytes: number | undefined): string {
  if (!bytes) {return '';}
  if (bytes < 1024) {return `${bytes} B`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Один прикреплённый файл
 */
function AttachmentItem({
  attachment,
  onRemove,
}: {
  attachment: Attachment;
  onRemove: () => void;
}) {
  const fileType = getFileType(attachment.mimeType);
  const isUploading = attachment.status === 'uploading';
  const hasError = attachment.status === 'error';

  return (
    <div
      className={cn(
        'relative group flex items-center gap-2 px-2 py-1.5 rounded-lg border',
        'bg-background hover:bg-muted/50 transition-colors',
        hasError && 'border-destructive bg-destructive/5'
      )}
    >
      {/* Иконка или превью */}
      <div className="flex-shrink-0 w-8 h-8 rounded overflow-hidden bg-muted flex items-center justify-center">
        {fileType === 'image' && attachment.url ? (
          <img
            src={attachment.url}
            alt={attachment.filename || 'Preview'}
            className="w-full h-full object-cover"
          />
        ) : fileType === 'image' ? (
          <Image className="h-4 w-4 text-muted-foreground" />
        ) : fileType === 'document' ? (
          <FileText className="h-4 w-4 text-muted-foreground" />
        ) : (
          <FileIcon className="h-4 w-4 text-muted-foreground" />
        )}
      </div>

      {/* Информация о файле */}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">
          {attachment.filename || 'Файл'}
        </div>
        {attachment.size && (
          <div className="text-[10px] text-muted-foreground">
            {formatFileSize(attachment.size)}
          </div>
        )}
      </div>

      {/* Прогресс загрузки */}
      {isUploading && (
        <div className="flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin text-primary" />
          {attachment.uploadProgress !== undefined && (
            <span className="text-[10px] text-muted-foreground">
              {attachment.uploadProgress}%
            </span>
          )}
        </div>
      )}

      {/* Кнопка удаления */}
      <button
        type="button"
        onClick={onRemove}
        className={cn(
          'absolute -top-1.5 -right-1.5 p-0.5 rounded-full',
          'bg-muted hover:bg-destructive hover:text-destructive-foreground',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'focus:opacity-100 focus:outline-none'
        )}
      >
        <X className="h-3 w-3" />
      </button>

      {/* Индикатор ошибки */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-destructive/10 rounded-lg">
          <span className="text-[10px] text-destructive">Ошибка загрузки</span>
        </div>
      )}
    </div>
  );
}

/**
 * Список прикреплённых файлов
 */
export function AttachmentPreview({
  attachments,
  onRemove,
  className,
}: AttachmentPreviewProps) {
  if (attachments.length === 0) {return null;}

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {attachments.map((attachment) => (
        <AttachmentItem
          key={attachment.id}
          attachment={attachment}
          onRemove={() => onRemove(attachment.id)}
        />
      ))}
    </div>
  );
}
